import { loadingReducers, LoadingState } from './base/baseModelStore';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { MaintenanceLogEntry, MaintenanceDuration } from '@api-model/models';
import { AppThunk } from '@root/store/store';
import { httpClient } from '@infrastructure/http/HttpClient';
import { PagedResponseModel } from '@api-model/pagedResponse';
import { unionWith } from 'lodash';

export interface MaintenanceLogState extends LoadingState {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  durationFilter: MaintenanceDuration;
  history: MaintenanceLogEntry[];
}

const DEFAULT_STATE: MaintenanceLogState = {
  loading: false,
  error: null,
  currentPage: 1,
  pageSize: 10,
  totalItems: 0,
  totalPages: 0,
  durationFilter: MaintenanceDuration.Quarter,
  history: [],
};

const loadingHistorySuccess = (
  state: MaintenanceLogState,
  { payload }: PayloadAction<PagedResponseModel<MaintenanceLogEntry>>
) => {
  const newHistory =
    payload.page === 1 ? payload.items : unionWith(state.history, payload.items, (a, b) => a.date === b.date);

  state.currentPage = payload.page;
  state.totalItems = payload.total;
  state.totalPages = Math.ceil(payload.total / payload.pageSize);
  state.history = newHistory;
  state.loading = false;
};

const slice = createSlice({
  name: 'maintenanceLog',
  initialState: DEFAULT_STATE,
  reducers: {
    ...loadingReducers,
    loadingHistorySuccess,
    setDurationFilter(state: MaintenanceLogState, payload: PayloadAction<MaintenanceDuration>) {
      state.durationFilter = payload.payload;
    },
    resetAll(state: MaintenanceLogState) {
      const { ...stateData } = state;
      state = { ...stateData, ...DEFAULT_STATE };
    },
    resetWithNoFilterChange(state: MaintenanceLogState) {
      const { durationFilter } = state;
      state = { ...DEFAULT_STATE, durationFilter };
    },
  },
});

const actions = slice.actions;

const getMaintenanceHistory = (
  siteId: number,
  page: number,
  pageSize: number,
  forLast: MaintenanceDuration
): AppThunk => async (dispatch) => {
  try {
    dispatch(actions.startLoading());
    const url = `maintenance/${siteId}?forLast=${forLast}&page=${page}&pageSize=${pageSize}`;
    const { data } = await httpClient.get<PagedResponseModel<MaintenanceLogEntry>>(url);
    dispatch(actions.loadingHistorySuccess(data));
  } catch (err) {
    dispatch(actions.loadingFailed(err.toString()));
  }
};

const addMaintenanceNote = (siteId: number, text: string): AppThunk => async (dispatch, getState) => {
  try {
    dispatch(actions.startLoading());
    const { status } = await httpClient.post(`maintenance/${siteId}`, {
      Text: text,
    });

    if (status === 200) {
      const { pageSize, durationFilter } = getState().maintenanceLog;
      dispatch(getMaintenanceHistory(siteId, 1, pageSize, durationFilter));
    }
  } catch (err) {
    dispatch(actions.loadingFailed(err.toString()));
  }
};

const thunks = {
  getMaintenanceHistory,
  addMaintenanceNote,
};

export const maintenanceLogEntity = {
  reducer: slice.reducer,
  actions,
  thunks,
} as {
  reducer: typeof slice.reducer;
  actions: typeof actions;
  thunks: typeof thunks;
};
